import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/button";
import {
  Lightbulb,
  Plus,
  Sparkles,
  BookOpen,
  ArrowRight,
  Filter,
} from "lucide-react";
import { EmptyStateProps } from "./types";

const EmptyState: React.FC<EmptyStateProps> = ({
  hasFilters,
  onAddInsight,
  onAIExtract,
  onRunEngagement,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  // If filters are active, show a simpler "no results" state
  if (hasFilters) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-6 min-h-[300px]">
        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
          <Filter className="h-6 w-6 text-gray-400" />
        </div>
        <h3 className="text-base font-semibold text-gray-900 mb-1.5">
          {t("no_matching_insights") || "No matching insights"}
        </h3>
        <p className="text-sm text-muted-foreground max-w-sm mb-5">
          {t("no_insights_match_filters") ||
            "No insights match your current filters. Try adjusting your search or filters."}
        </p>
        <Button variant="outline" size="sm" onClick={onClearFilters}>
          <Filter className="h-3.5 w-3.5 me-2" />
          {t("clear_filters") || "Clear Filters"}
        </Button>
      </div>
    );
  }

  // Full onboarding empty state
  return (
    <div className="flex flex-col items-center justify-center text-center p-6 min-h-[400px]">
      {/* Hero Icon */}
      <div className="relative mb-4">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center shadow-sm">
          <Lightbulb className="h-8 w-8 text-amber-500" />
        </div>
        {/* Decorative sparkles */}
        <div className="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center">
          <Sparkles className="h-2.5 w-2.5 text-violet-500" />
        </div>
        <div className="absolute -bottom-1 -start-1 w-4 h-4 rounded-full bg-amber-200 flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
        </div>
      </div>

      {/* Title & Description */}
      <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
        {t("your_insights_journey") || "Your insights journey starts here"}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md mb-6 leading-relaxed">
        {t("insights_empty_description") ||
          'Insights are the "aha moments" from your activities, conversations, and reflections that guide your growth and development.'}
      </p>

      {/* Action Cards */}
      <div className="grid gap-2.5 w-full max-w-md">
        {/* Add Manually */}
        <button
          onClick={onAddInsight}
          className="flex items-center gap-3 p-3 rounded-xl border border-amber-100 bg-gradient-to-r rtl:bg-gradient-to-l from-amber-50/50 to-white hover:from-amber-50 hover:to-amber-50/50 hover:border-amber-200 transition-all text-start group"
        >
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
            <Plus className="h-4 w-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900">
              {t("add_first_insight") || "Add your first insight manually"}
            </h4>
            <p className="text-xs text-muted-foreground">
              {t("capture_thought_or_learning") || "Capture a thought, quote, or learning"}
            </p>
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-amber-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
        </button>

        {/* AI Extract */}
        <button
          onClick={onAIExtract}
          className="flex items-center gap-3 p-3 rounded-xl border border-violet-100 bg-gradient-to-r rtl:bg-gradient-to-l from-violet-50/50 to-white hover:from-violet-50 hover:to-violet-50/50 hover:border-violet-200 transition-all text-start group"
        >
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 group-hover:bg-violet-200 transition-colors">
            <Sparkles className="h-4 w-4 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900">
              {t("extract_with_ai") || "Let AI extract insights from your notes"}
            </h4>
            <p className="text-xs text-muted-foreground">
              {t("paste_notes_ai_finds") || "Paste notes and AI will find key insights"}
            </p>
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-violet-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
        </button>

        {/* Run Engagement */}
        <button
          onClick={onRunEngagement}
          className="flex items-center gap-3 p-3 rounded-xl border border-primary/10 bg-gradient-to-r rtl:bg-gradient-to-l from-primary/5/50 to-white hover:from-primary/5 hover:to-primary/5/50 hover:border-primary/20 transition-all text-start group"
        >
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
            <BookOpen className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900">
              {t("run_engagement_to_capture") || "Run an engagement to capture insights"}
            </h4>
            <p className="text-xs text-muted-foreground">
              {t("insights_captured_automatically") || "You can capture your insights during activities"}
            </p>
          </div>
          <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
        </button>
      </div>

      {/* Tips Section */}
      <div className="mt-6 pt-4 border-t border-gray-100 w-full max-w-md">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-gray-600">{t("tip") || "Tip"}:</span>{" "}
          {t("insight_tip") ||
            "The best insights come from regular reflection. Try to capture at least one insight from each learning experience."}
        </p>
      </div>
    </div>
  );
};

export default EmptyState;
